import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { currentHistoryIndex, loadTrail, resolveBack } from './navTrail';

export interface UseBackOptions {
  /** Standardliste des Moduls — Ziel bei Direktaufruf (Link, neuer Tab) */
  fallback: string;
  /** Name der Standardliste, z. B. „Berichte" */
  fallbackLabel?: string;
  /** Pfad des Dokuments, wenn die Seite eine Unterseite davon ist (Standard: aktueller Pfad) */
  self?: string;
}

export interface BackTarget {
  go: () => void;
  /** „Zurück zu Berichte" bzw. „Zurück" */
  label: string;
  /** false = Direktaufruf, es geht in die Standardliste */
  viaHistory: boolean;
}

/**
 * Rücksprung dorthin, wo der Anwender herkam (Jörg 2026-07-13, präzisiert 2026-09-29,
 * Regel „Dokument-Navigation" R2). Geht immer im Verlauf zurück — nie als neue Seite auf
 * die Liste —, damit Filter, Reiter und Scrollposition der Liste erhalten bleiben.
 *
 * Löst bauberichte/lib/useBack ab. `state.from` wird nicht mehr ausgewertet: Es führte
 * vorwärts auf eine frische Liste ohne Filter.
 */
export function useBack(opts: string | UseBackOptions): BackTarget {
  const o: UseBackOptions = typeof opts === 'string' ? { fallback: opts } : opts;
  const { fallback, fallbackLabel, self } = o;
  const navigate = useNavigate();
  const location = useLocation();
  const selfPath = self ?? location.pathname;

  // Bei jedem Ortswechsel neu lesen (location.key ändert sich auch bei Filterwechsel).
  const resolved = useMemo(
    () => resolveBack(loadTrail(), currentHistoryIndex() ?? -1, selfPath),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selfPath, location.key],
  );

  const go = useCallback(() => {
    // Beim Klick neu auflösen — die Spur kann sich seit dem Rendern geändert haben.
    const now = resolveBack(loadTrail(), currentHistoryIndex() ?? -1, selfPath);
    if (now) navigate(now.delta);
    else navigate(fallback, { replace: true });
  }, [navigate, fallback, selfPath]);

  const target = resolved ? resolved.target.label : fallbackLabel;
  return { go, label: target ? `Zurück zu ${target}` : 'Zurück', viaHistory: resolved != null };
}
